import React, { useCallback, useState, useEffect, useRef } from "react";
import styles from "./CitySelect.module.scss";
import classNames from "classnames";
import TextInput from "../text-input/TextInput";
import { loadGoogleMaps } from "../../utils/loadGoogleMaps";

interface CitySelectProps {
  id: string;
  label?: string;
  name: string;
  value?: string;
  hasError?: boolean;
  errorText?: string;
  placeholder: string;
  onChange?: (value: string, place?: any) => void;
  disabled?: boolean;
}

const CitySelect: React.FC<CitySelectProps> = (props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState(props.value || "");
  const [cityOptions, setCityOptions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCity, setSelectedCity] = useState<any>(null);
  const [selectedCityName, setSelectedCityName] = useState<string>("");
  const selectedCityNameRef = useRef<string>("");
  const searchValueRef = useRef<string>(searchValue);

  //keep refs in sync
  useEffect(() => {
    selectedCityNameRef.current = selectedCityName;
  }, [selectedCityName]);

  useEffect(() => {
    searchValueRef.current = searchValue;
  }, [searchValue]);

  const autocompleteService = useRef<any>(null);
  const placesService = useRef<any>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    loadGoogleMaps(process.env.REACT_APP_GOOGLE_MAPS_API_KEY!).then(() => {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
      placesService.current = new window.google.maps.places.PlacesService(
        document.createElement("div"),
      );
    });

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  useEffect(() => {
    setSearchValue(props.value || "");
  }, [props.value]);

  const searchCities = useCallback((query: string) => {
    if (!autocompleteService.current || query.length < 2) {
      setCityOptions([]);
      return;
    }

    setIsLoading(true);

    const request: any = {
      input: query,
      types: ["(cities)"],
      componentRestrictions: {
        country: "ge",
      },
    };

    autocompleteService.current.getPlacePredictions(
      request,
      (predictions: any[] | null, status: any) => {
        setIsLoading(false);

        if (predictions) {
          setCityOptions(predictions);
        } else {
          setCityOptions([]);
        }
      },
    );
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (selectedCityName) {
      setSelectedCityName("");
      setSelectedCity(null);
    }
    setSearchValue(value);
    searchValueRef.current = value;
    setIsOpen(true);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      searchCities(value);
    }, 750);
  };

  const handleOptionClick = (prediction: any) => {
    const cityName = prediction.structured_formatting.main_text;
    setSelectedCityName(cityName);
    selectedCityNameRef.current = cityName;
    setSearchValue(cityName);
    setIsOpen(false);

    // Get full place details including geometry
    if (placesService.current) {
      placesService.current.getDetails(
        { placeId: prediction.place_id },
        (place: any, status: any) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            place
          ) {
            setSelectedCity(place);
            if (props.onChange) {
              props.onChange(cityName, place);
            }
          } else {
            if (props.onChange) {
              props.onChange(cityName);
            }
          }
        },
      );
    } else {
      if (props.onChange) {
        props.onChange(cityName);
      }
    }
  };

  const handleFocus = () => {
    setIsOpen(true);
    if (searchValue.length >= 2) {
      searchCities(searchValue);
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      setIsOpen(false);
      //if user leaves the field without selecting a suggestion clear the input
      const latestSelected = selectedCityNameRef.current;
      const latestSearch = searchValueRef.current;
      if (!latestSelected || latestSearch.trim() !== latestSelected) {
        if (latestSearch.trim().length > 0) {
          setSearchValue("");
          searchValueRef.current = "";
          if (props.onChange) {
            props.onChange("", undefined);
          }
        }
        setSelectedCity(null);
        setSelectedCityName("");
        selectedCityNameRef.current = "";
      }
    }, 200);
  };

  const selectClasses = classNames({
    [styles.select]: true,
    [styles.hasError]: props.hasError,
    [styles.disabled]: props.disabled,
  });

  return (
    <div className={styles.container}>
      {props.label && (
        <label
          htmlFor={props.id}
          className={props.disabled ? styles.disabled : ""}
        >
          {props.label}
        </label>
      )}

      <div className={selectClasses}>
        <TextInput
          id={props.id}
          type="text"
          name={props.name}
          value={searchValue}
          onChange={handleSearchChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={props.placeholder}
          disabled={props.disabled}
        />

        {isOpen && (
          <div className={styles.optionsList}>
            {isLoading ? (
              <div className={styles.loading}>Loading cities...</div>
            ) : cityOptions.length > 0 ? (
              cityOptions.map((option, index) => (
                <div
                  key={index}
                  className={styles.option}
                  onClick={() => handleOptionClick(option)}
                >
                  {option.description}
                </div>
              ))
            ) : searchValue.length >= 2 ? (
              <div className={styles.noResults}>No cities found</div>
            ) : (
              <div className={styles.hint}>
                Type at least 2 characters to search cities
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CitySelect;
